import { useEffect, useRef, useState } from "react";
import { LANG_LABELS, SUPPORTED, useT, type Lang } from "../i18n";
import { api } from "../api";
import { tg } from "../telegram";

/**
 * Compact language picker pinned to the top corner of every page.
 * Shows the current language as a single emoji button; click reveals a tiny
 * popup with all three options. The choice is kept in localStorage and saved on
 * the server (PATCH /api/me/language), so /api/me brings it back on the next
 * start and on the user's other devices instead of the Telegram app language.
 */
export default function LanguageSwitcher() {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = async (next: Lang) => {
    setOpen(false);
    if (next === lang || busy) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    setLang(next);
    setBusy(true);
    try {
      await api.patch("/api/me/language", { language_code: next });
    } catch {
      // best-effort: UI already swapped
    } finally {
      setBusy(false);
    }
  };

  const current = LANG_LABELS[lang];

  return (
    <div className="lang-corner" ref={ref}>
      <button
        type="button"
        className="lang-corner-btn"
        aria-label={t("settings.language")}
        title={t("settings.language")}
        onClick={() => setOpen(o => !o)}
        disabled={busy}
      >
        <span className="lang-flag">{current.emoji}</span>
        <span className="lang-caret">▾</span>
      </button>
      {open && (
        <div className="lang-corner-popup" role="menu">
          {SUPPORTED.map(code => {
            const meta = LANG_LABELS[code];
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                className={"lang-corner-item" + (active ? " active" : "")}
                onClick={() => choose(code)}
                role="menuitem"
              >
                <span className="lang-flag">{meta.emoji}</span>
                <span className="lang-name">{meta.label}</span>
                {active && <span className="lang-check">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
